// ~/mimi-network-mvp/shared/design-system/colors.ts
// Sushi-Inspired Color Palette
// Based on authentic sushi colors from SchemeColor.com

export const SUSHI_PALETTE = {
  rifleGreen: '#47482E',    // Dark seaweed/nori
  appleGreen: '#8EB700',    // Fresh wasabi/edamame  
  bone: '#DBDCC9',          // Rice white
  gargoyleGas: '#FBE642',   // Pickled ginger yellow
  rybOrange: '#F3A004',     // Salmon orange
  kenyanCopper: '#6E2602'   // Soy sauce brown
} as const;

// Platform-specific color combinations
export const PLATFORM_COLORS = {
  'aikaju.io': {
    primary: SUSHI_PALETTE.rybOrange,     // #F3A004 (salmon)
    secondary: SUSHI_PALETTE.gargoyleGas, // #FBE642 (ginger)
    theme: 'Salmon & Ginger',
    guardian: 'Mikako',
    emoji: '🍊'
  },
  'aibento.app': {
    primary: SUSHI_PALETTE.kenyanCopper,  // #6E2602 (soy sauce)
    secondary: SUSHI_PALETTE.rifleGreen,  // #47482E (nori)
    theme: 'Soy Sauce & Nori',
    guardian: 'Ichiko',
    emoji: '🍚'
  },
  'umujo.ai': {
    primary: SUSHI_PALETTE.appleGreen,    // #8EB700 (wasabi)
    secondary: SUSHI_PALETTE.bone,        // #DBDCC9 (rice)
    theme: 'Wasabi & Rice',
    guardian: 'Momoko',
    emoji: '🍑'
  },
  'aido.dev': {
    primary: SUSHI_PALETTE.bone,          // #DBDCC9 (rice)
    secondary: SUSHI_PALETTE.appleGreen,  // #8EB700 (wasabi)
    theme: 'Rice & Wasabi',
    guardian: 'Komomo',
    emoji: '🌸'
  },
  'kampai.tech': {
    primary: SUSHI_PALETTE.gargoyleGas,   // #FBE642 (ginger)
    secondary: SUSHI_PALETTE.rybOrange,   // #F3A004 (salmon)
    theme: 'Ginger & Salmon',
    guardian: 'Budouchan',
    emoji: '🍇'
  },
  'mimi.network': {
    primary: SUSHI_PALETTE.rifleGreen,    // #47482E (nori)
    secondary: SUSHI_PALETTE.kenyanCopper,// #6E2602 (soy sauce)
    theme: 'Nori & Soy Sauce',
    guardian: 'Umeko',
    emoji: '🍱'
  }
} as const;

// BentoIcon invariant colors (always consistent across platforms)
export const BENTO_ICON_COLORS = {
  lines: SUSHI_PALETTE.rifleGreen,  // #47482E (nori)
  dots: SUSHI_PALETTE.appleGreen    // #8EB700 (wasabi)
} as const;

// Helper function to get platform configuration
export const getSushiPlatformConfig = (platform: keyof typeof PLATFORM_COLORS) => {
  return PLATFORM_COLORS[platform] || PLATFORM_COLORS['aikaju.io'];
};

// Utility functions for color manipulation
export const addOpacity = (hexColor: string, opacity: number): string => {
  const hex = hexColor.replace('#', '');
  const opacityHex = Math.round(opacity * 255).toString(16).padStart(2, '0');
  return `#${hex}${opacityHex}`;
};

export const getRGBA = (hexColor: string, opacity: number): string => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};
// Export platform type for authentication system
export type PlatformName = keyof typeof PLATFORM_COLORS;
export type GuardianName = typeof PLATFORM_COLORS[PlatformName]['guardian'];
